import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { getImagenUrl } from 'shared/api';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.2;

function urlResolve(u) {
  if (!u) return null;
  return u.startsWith('http') || u.startsWith('blob:') ? u : getImagenUrl(u);
}

export default function VisorImagen({ images, currentIndex, onClose }) {
  const [idx, setIdx] = useState(currentIndex || 0);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, posStartX: 0, posStartY: 0 });

  const list = Array.isArray(images) ? images : [{ url: images }];
  const current = list[idx] || list[0];
  const isVideo = current?.tipo === 'video';
  const src = current
    ? isVideo
      ? urlResolve(current.url)
      : urlResolve(current.url || current.imagen_url)
    : null;

  useEffect(() => {
    setZoom(1);
    setPos({ x: 0, y: 0 });
  }, [idx]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && list.length > 1)
        setIdx((i) => (i - 1 + list.length) % list.length);
      if (e.key === 'ArrowRight' && list.length > 1) setIdx((i) => (i + 1) % list.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, list.length]);

  const handleWheel = useCallback((e) => {
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom((z) => {
      const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z + delta));
      if (next <= 1) setPos({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const handlePointerDown = useCallback(
    (e) => {
      e.stopPropagation();
      if (zoom <= 1) return;
      setDragging(true);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        posStartX: pos.x,
        posStartY: pos.y,
      };
    },
    [zoom, pos]
  );

  const handlePointerMove = useCallback((e) => {
    setDragging((d) => {
      if (!d) return d;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      setPos({
        x: dragRef.current.posStartX + dx,
        y: dragRef.current.posStartY + dy,
      });
      return d;
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    setDragging(false);
  }, []);

  useEffect(() => {
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  const handleImageClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (isVideo) return;
      setZoom((z) => {
        if (z === 1) {
          setPos({ x: 0, y: 0 });
          return 2;
        }
        setPos({ x: 0, y: 0 });
        return 1;
      });
    },
    [isVideo]
  );

  if (!current || !src) return null;

  const imgProps = isVideo
    ? {
        src,
        controls: true,
        playsInline: true,
        autoPlay: true,
        loop: true,
        style: {
          maxWidth: '92vw',
          maxHeight: '90vh',
          borderRadius: '12px',
          boxShadow: '0 4px 30px rgba(0,0,0,0.3)',
        },
      }
    : {};

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.92)',
        zIndex: 4000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
      onWheel={handleWheel}
    >
      <div
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          display: 'flex',
          gap: '8px',
          zIndex: 10,
        }}
      >
        {zoom > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setZoom(1);
              setPos({ x: 0, y: 0 });
            }}
            style={{ ...BTN, fontSize: '12px' }}
          >
            1:1
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          style={BTN}
        >
          <FiX size={20} />
        </button>
      </div>

      {list.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIdx((i) => (i - 1 + list.length) % list.length);
            }}
            style={{ ...ARR, left: '10px' }}
          >
            <FiChevronLeft size={28} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIdx((i) => (i + 1) % list.length);
            }}
            style={{ ...ARR, right: '10px' }}
          >
            <FiChevronRight size={28} />
          </button>
        </>
      )}

      <div
        onClick={isVideo ? undefined : (e) => e.stopPropagation()}
        onPointerDown={handlePointerDown}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          overflow: 'hidden',
          width: '100%',
          cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : isVideo ? 'default' : 'pointer',
          userSelect: 'none',
        }}
      >
        {isVideo ? (
          <video key={src} {...imgProps} />
        ) : (
          <img
            key={`${src}-${idx}`}
            src={src}
            alt=""
            draggable={false}
            onClick={handleImageClick}
            style={{
              maxWidth: '95vw',
              maxHeight: '90vh',
              objectFit: 'contain',
              transform: `scale(${zoom}) translate(${pos.x / zoom}px, ${pos.y / zoom}px)`,
              transition: dragging ? 'none' : 'transform 0.15s',
              borderRadius: '12px',
              boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
            }}
          />
        )}
      </div>

      {list.length > 1 && (
        <div
          style={{
            display: 'flex',
            gap: '8px',
            padding: '8px 16px',
            overflowX: 'auto',
            flexShrink: 0,
            background: 'rgba(0,0,0,0.5)',
            borderRadius: '10px',
            marginBottom: '12px',
            maxWidth: '90vw',
          }}
        >
          {list.map((img, i) => (
            <div
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                setIdx(i);
              }}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '6px',
                overflow: 'hidden',
                cursor: 'pointer',
                flexShrink: 0,
                border: i === idx ? '2px solid #fff' : '2px solid transparent',
                opacity: i === idx ? 1 : 0.5,
              }}
            >
              {img.tipo === 'video' ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: '#333',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    color: '#aaa',
                  }}
                >
                  VID
                </div>
              ) : (
                <img
                  src={urlResolve(img.url || img.imagen_url)}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {list.length > 1 && (
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', paddingBottom: '6px' }}>
          {idx + 1} / {list.length}
        </div>
      )}
    </div>
  );
}

const BTN = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  border: 'none',
  background: 'rgba(255,255,255,0.15)',
  color: '#fff',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '16px',
};

const ARR = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
  width: '44px',
  height: '44px',
  borderRadius: '50%',
  border: 'none',
  background: 'rgba(255,255,255,0.12)',
  color: '#fff',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '28px',
};
